import { cn } from '@/lib/utils/cn';
import { SrTable, ZONE_LABEL, cssVars, formatPct, zoneOf, type Thresholds } from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * ScoreBar — a criterion's 0..100 score against the two gates.
 *
 * `mini` (prototype `miniBullet()`): an 8px track tinted No-Go /
 * Conditional / Go at the thresholds, the score as an ink bar inset 2px.
 * It sits beside its number in rail rows and evidence lists, so it is an
 * image whose label reads the score, the zone and the gates.
 *
 * `full` (the criterion detail's bullet): the three zones labelled, the
 * score as a 10px ink measure across their middle, the AI's reading as a
 * marked line when it differs from the confirmed score, and an axis at 0,
 * Conditional, Go and 100. Ships a visually hidden table.
 */
export interface ScoreBarProps {
  /** Confirmed score 0..100; null when not scored. */
  value: number | null;
  thresholds: Thresholds;
  variant?: 'mini' | 'full';
  /** The AI's proposed score (full only); drawn when it differs. */
  aiValue?: number | null;
  /** What is scored ("UAT"), for the accessible name. */
  label?: string;
  motionKey?: string | number;
  className?: string;
}

export function ScoreBar({
  value,
  thresholds,
  variant = 'mini',
  aiValue = null,
  label,
  motionKey,
  className,
}: ScoreBarProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const c = thresholds.conditional;
  const g = thresholds.go;
  const zone = zoneOf(value, thresholds);
  const what = label ? `${label} score` : 'Score';
  const read =
    value === null
      ? `${what}: not scored`
      : `${what}: ${formatPct(value)}, ${ZONE_LABEL[zone]} (Conditional at ${c}, Go at ${g})`;

  if (variant === 'mini') {
    return (
      <span
        key={runKey}
        role="img"
        aria-label={read}
        className={cn(
          'chart-mini-bullet relative block h-2 overflow-hidden rounded',
          animate && 'chart-anim',
          className,
        )}
        style={cssVars({ '--c': `${c}%`, '--g': `${g}%` })}
      >
        {value === null ? null : (
          <i
            className="a-grow absolute inset-y-0.5 left-0 rounded-r-[3px] bg-ink-900"
            style={{ width: `${value}%` }}
          />
        )}
      </span>
    );
  }

  const ai = aiValue !== null && aiValue !== undefined && aiValue !== value ? aiValue : null;
  const rows: Array<[string, string]> = [
    ['Score', value === null ? 'Not scored' : formatPct(value)],
    ['Zone', ZONE_LABEL[zone]],
    ['Conditional at', `${c}%`],
    ['Go at', `${g}%`],
  ];
  if (ai !== null) rows.splice(1, 0, ['AI reading', formatPct(ai)]);

  return (
    <div
      key={runKey}
      className={cn('relative pb-[22px] pt-[18px]', animate && 'chart-anim', className)}
    >
      <div aria-hidden>
        <div className="relative flex h-7 gap-0.5 overflow-hidden rounded-[6px]">
          {(
            [
              ['No-Go', 'bg-posture-critical-tint', { width: `${c}%` }],
              ['Conditional', 'bg-posture-at-risk-tint', { width: `${g - c}%` }],
              ['Go', 'bg-posture-healthy-tint', { flex: 1 }],
            ] as const
          ).map(([text, bg, style]) => (
            <span
              key={text}
              className={cn(
                'h-full overflow-hidden whitespace-nowrap pl-1.5 text-[10px] font-medium leading-7 text-ink-500',
                bg,
              )}
              style={style}
            >
              {text}
            </span>
          ))}
        </div>
        {value === null ? null : (
          <span
            className="a-grow absolute left-0 top-[27px] h-2.5 rounded-r bg-ink-900"
            style={{ width: `${value}%` }}
          />
        )}
        {ai !== null ? (
          <span
            className="a-fade absolute top-3 h-10 w-0 border-l-2 border-ai-ink"
            style={{ left: `${ai}%` }}
          >
            <span className="absolute -left-3.5 -top-4 whitespace-nowrap text-[10px] font-semibold text-ai-ink">
              AI {Math.round(ai)}
            </span>
          </span>
        ) : null}
        <div className="relative mt-1 h-3.5 text-[10px] font-medium text-ink-500">
          {[0, c, g, 100].map((v, i) => (
            <span key={`${v}-${i}`} className="absolute -translate-x-1/2" style={{ left: `${v}%` }}>
              {v}
            </span>
          ))}
        </div>
      </div>
      <SrTable caption={`${what} against the gates`} head={['Measure', 'Value']} rows={rows} />
    </div>
  );
}
